// View switching
import React, { useRef } from "react";

import * as StateLib from "@/Utils/StateLib";
import { Flex, Menu } from "@theorgchartdev/storybook";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { useNavigate } from "react-router-dom";
import { ChartSettingsSectionRoutes } from "@/providers/NavigationProvider";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import { useRouteState } from "@/hooks/useRouteState";
import { GetWSMgr } from "@/Utils/StateLib";
import { DoBackgroundSavePrompt } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/Backgrounds.utils";
import { appstr } from "@coreapp/utils/appstr";
import { enumChartTypes, enumFeaturesList } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { kAdminUser } from "@coreapp/constants";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradePopover from "../../Composites/UpgradePopover/UpgradePopover";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

export const CheckChangeChartView = (callbackFn, openCtrlRef) => {
  const wsMgr = GetWSMgr();
  if (wsMgr.IsBackgroundDirty()) {
    DoBackgroundSavePrompt(callbackFn, callbackFn);
    openCtrlRef?.current?.setOpen(false);
  } else {
    callbackFn();
  }
};

export const ChangeChartView = target => {
  let wsMgr = StateLib.GetWSMgr();
  let eventTracker = wsMgr.GetUsageTracker();
  eventTracker.ReportEvent("chart_mode.view.opened");
  wsMgr.DoSetView(target.value);
  StateLib.ReRenderCanvas();
};

export const ChangeDisplayMode = target => {
  var theMode = enumChartTypes.standard;
  let wsMgr = StateLib.GetWSMgr();
  if (!wsMgr) return;
  var canvasPanel = wsMgr.fCanvasPanel;
  var plm = canvasPanel.GetPageLayoutMgr();
  let targetMode = target.value.split("-")[1];
  plm.SetChartType(targetMode);
  if (theMode == enumChartTypes.card && Globals._svrChartMgr.GetNumChainLevels() > 0) {
    Globals._svrChartMgr.SetNumChainLevels(0);
    wsMgr.DoChartDataQuery();
  } else wsMgr.ReloadCurrentSubChart();
};

export const GetViewName = (viewID, muteAsterisk = false) => {
  let wsMgr = StateLib.GetWSMgr();
  let ctm = wsMgr.GetChartTemplatesMgr();
  let templateResult = ctm.FindTemplateByIDAllSets(viewID);
  let templateObj = templateResult["template"]; //TODO: more elegant handling of deleted views
  let localizationExists = Globals._svrChartMgr.LocalizationExists("views", viewID); //When display to end user
  let displayName = localizationExists
    ? Globals._svrChartMgr.Localize("views", viewID, localizationExists)
    : templateObj.GetTemplateName();

  // This is used when making the name editable
  if (muteAsterisk) {
    return displayName;
  }

  displayName += templateObj.IsDirty() ? "*" : "";
  return displayName;
};

const BuildViewOptions = (
  viewMgr,
  handleOpenViewLibrary,
  navigate,
  openCtrlRef,
  canAccessViewLibrary,
  onViewChange
) => {
  let viewOptions = [];
  if (!viewMgr) return viewOptions;
  let uInfo = StateLib.GetUserInfo();
  const doc = StateLib.GetDoc();
  let allowedViewList = viewMgr.GetAllowedViewList();
  let lastViewIdx = 0;
  for (const [index, viewID] of allowedViewList.entries()) {
    let viewOption = {};
    viewOption.dataTestId = `mtb-views-menu-${viewID}`;
    viewOption.label = GetViewName(viewID);
    viewOption.value = viewID;
    viewOption.clickHandler = () =>
      CheckChangeChartView(() => onViewChange(viewOption), openCtrlRef);
    viewOptions.push(viewOption);
    lastViewIdx = index;
  }
  if ([kAdminUser].includes(uInfo.GetUserRole()) && canAccessViewLibrary) {
    viewOptions.push({
      dataTestId: chartModeTestId("sidebar", "view-rules-manage-views-btn"),
      label: appstr("Manage available views"),
      value: "manage-available-views",
      icon: "gear",
      clickHandler: () => {
        const chartID = doc.GetChartID();
        const route = `/settings/chart/${chartID}/${ChartSettingsSectionRoutes.Presentation}`;
        navigate(route);
      }
    });
    viewOptions[lastViewIdx].divider = true;
  }
  if (canAccessViewLibrary) {
    viewOptions.push({
      dataTestId: "mtb-view-library",
      label: doc?.IsPositionDocument() ? appstr("Position view library") : appstr("View library"),
      value: "view-library",
      clickHandler: handleOpenViewLibrary,
      icon: "books"
    });
    viewOptions[lastViewIdx].divider = true;
  }
  return viewOptions;
};

/**
 * @param {{ position?: import("@floating-ui/react").Placement, popoverProps?: Record<string, any> }} [props]
 */
export const ViewMenu = ({ position = "bottom-start", popoverProps } = {}) => {
  const navigate = useNavigate();
  const { setShowViewLibrary } = useViewManagerContext();
  const { setParam: setRouteParam } = useRouteState();
  const { canAccessViewLibrary, canAccessTemplates, showPricingAndPackaging } =
    usePermissionCheck();
  const wsMgrContext = useWSMgrContext();
  const openCtrlRef = useRef(null);
  const wsMgr = wsMgrContext.wsMgr.state;
  if (!wsMgr) return;

  const doc = wsMgr.GetDoc();
  if (!doc) return;

  const viewMgr = doc.GetViewMgr();
  if (!viewMgr) return;

  let activeTemplate = doc.GetActiveTemplate();
  let curViewID = activeTemplate ? activeTemplate.GetTemplateID() : -1;
  let viewMenuLabel = curViewID ? GetViewName(curViewID) : "No view selected";
  let chartType = doc.GetPageLayoutMgr().GetChartType();

  const handleOpenViewLibrary = () => {
    openCtrlRef.current?.setOpen(false);
    setShowViewLibrary(true);
  };

  // URL-driven: writing ?view= is the single trigger. ViewManagerProvider's
  // URL → coreapp effect picks up the change and calls DoSetView. The click
  // handler never calls DoSetView directly, so React Router is always the
  // source of truth for the active view.
  const handleViewChange = target => {
    const wsMgr = GetWSMgr();
    wsMgr?.GetUsageTracker?.().ReportEvent("chart_mode.view.opened");
    setRouteParam("view", target.value == null ? null : String(target.value));
  };

  return (
    <Menu
      openCtrlRef={openCtrlRef}
      stayOpenOnOptionSelect={true}
      listWidth={"medium"}
      buttonSize={"small"}
      listProps={{
        footerNode:
          canAccessTemplates && showPricingAndPackaging && !canAccessViewLibrary ? (
            <Flex width={"100%"} padding={"8px 12px"} justifyContent={"center"}>
              <UpgradePopover
                pillLabel={appstr("Upgrade to add views")}
                featureKey={enumFeaturesList.promoteViews}
                popoverText={{
                  message: appstr("view library upgrade message"),
                  template: appstr("view library upgrade template"),
                  formatConfig: { bold1: { type: "bold" } },
                  replaceDict: { bold1: appstr("unlimited number of custom org chart views") }
                }}
                placement="left"
              />
            </Flex>
          ) : null
      }}
      listHeader={{
        label: appstr("Available views")
      }}
      triggerProps={{
        tooltipProps: { message: appstr("Views") },
        ariaLabel: "Available views dropdown menu",
        dataTestId: "mtb-views-menu-trigger"
      }}
      position={position}
      label={viewMenuLabel}
      value={[curViewID, "display-" + chartType]}
      options={BuildViewOptions(
        viewMgr,
        handleOpenViewLibrary,
        navigate,
        openCtrlRef,
        canAccessViewLibrary,
        handleViewChange
      )}
      dataTestId={"mtb-views-menu"}
      popoverProps={{
        ...popoverProps,
        floatingProps: {
          ...popoverProps?.floatingProps,
          "data-testid": chartModeTestId("sidebar", "view-rules-views-popover")
        }
      }}
    />
  );
};

export default ViewMenu;
